import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { hasFinancialAccess, isManagement, isDivisionUser, isContentCreator, isMuaAdmin, canManageContent, PBH_DIVISION_ID } from "./lib/constants";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PendingApproval from "./pages/PendingApproval";
import PendingUsers from "./pages/PendingUsers";
import { PortalLogin, PortalDashboard } from "./pages/Portal";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientStatement from "./pages/ClientStatement";
import Bookings from "./pages/Bookings";
import BookingDetail from "./pages/BookingDetail";
import CalendarPage from "./pages/CalendarPage";
import Tasks from "./pages/Tasks";
import Quotations from "./pages/Quotations";
import QuotationDetail from "./pages/QuotationDetail";
import Invoices from "./pages/Invoices";
import InvoiceDetail from "./pages/InvoiceDetail";
import Vendors from "./pages/Vendors";
import Wages from "./pages/Wages";
import Approvals from "./pages/Approvals";
import FinanceDashboard from "./pages/FinanceDashboard";
import Workspace from "./pages/Workspace";
import Content from "./pages/Content";
import Employees from "./pages/Employees";
import EmployeeDetail from "./pages/EmployeeDetail";
import DivisionsOverview from "./pages/DivisionsOverview";
import PrintReceipt from "./pages/PrintReceipt";
import PrintInvoice from "./pages/PrintInvoice";
import PrintQuotation from "./pages/PrintQuotation";
import PrintWagePayment from "./pages/PrintWagePayment";
import PrintClientStatement from "./pages/PrintClientStatement";
import Pricelist from "./pages/Pricelist";
import MasterProducts from "./pages/MasterProducts";
import Inquiry from "./pages/Inquiry";
import Reports from "./pages/Reports";
import PrintInquiry from "./pages/PrintInquiry";
import AuthCallback from "./pages/AuthCallback";
import Landing from "./pages/Landing";
import Consultation from "./pages/Consultation";
import RecycleBin from "./pages/RecycleBin";
import LandingCMS from "./pages/LandingCMS";
import Imports from "./pages/Imports";
import PrintBulkWageSlips from "./pages/PrintBulkWageSlips";
import Production from "./pages/Production";
import ProductDetail from "./pages/ProductDetail";
import PaketWeddingDWG from "./pages/PaketWeddingDWG";
import PublicVendors from "./pages/PublicVendors";
import VendorDetail from "./pages/VendorDetail";
import InquiryPool from "./pages/InquiryPool";
import BookingsTasks from "./pages/BookingsTasks";
import TasksBoard from "./pages/TasksBoard";
import OverloadPlanner from "./pages/OverloadPlanner";
// Schedules merged into CalendarPage (kalender & jadwal terpadu)

// PBH Manager helper — has GM-lite scope for PBH-related pages
const isPbhManager = (u) => u && u.role === "division_manager" && u.division_id === PBH_DIVISION_ID;
const isPbhMember  = (u) => u && u.division_id === PBH_DIVISION_ID;
const isDivManager = (u) => u && u.role === "division_manager";
const canManageOrDM = (u) => isManagement(u) || isDivManager(u);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen app-bg flex items-center justify-center text-sm text-[#8C9096]">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "pending") return <PendingApproval />;
  return children;
};
const AppRoot = () => {
  // Public landing when unauthenticated (Layout still requires auth via Protected below)
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen app-bg flex items-center justify-center text-sm text-[#8C9096]">Memuat...</div>;
  if (!user) return <Landing />;
  if (user.role === "pending") return <PendingApproval />;
  return <Layout />;
};
const Guard = ({ allow, children }) => {
  const { user } = useAuth();
  if (!user) return null;
  if (!allow(user)) return <Navigate to={isContentCreator(user) ? "/content" : (isDivisionUser(user) && !isMuaAdmin(user) && user.role !== "division_manager") ? "/workspace" : "/dashboard"} replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AuthRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

function AuthRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Handle Google OAuth callback synchronously (before Protected route checks run)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/portal" element={<PortalLogin />} />
          <Route path="/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/print/receipt/:id" element={<Protected><PrintReceipt /></Protected>} />
          <Route path="/print/invoice/:id" element={<Protected><PrintInvoice /></Protected>} />
          <Route path="/print/quotation/:id" element={<Protected><PrintQuotation /></Protected>} />
          <Route path="/print/wage-payment/:id" element={<Protected><PrintWagePayment /></Protected>} />
          <Route path="/print/client-statement/:id" element={<Protected><PrintClientStatement /></Protected>} />
          <Route path="/print/inquiry/:id" element={<Protected><PrintInquiry /></Protected>} />
          <Route path="/print/bulk-wages" element={<Protected><PrintBulkWageSlips /></Protected>} />
          <Route path="/catalog" element={<Navigate to="/paket-wedding-dwg" replace />} />
          <Route path="/paket-wedding-dwg" element={<PaketWeddingDWG />} />
          <Route path="/vendor" element={<PublicVendors />} />
          <Route path="/vendor/:slug" element={<VendorDetail />} />
          <Route path="/consultation" element={<Consultation />} />
          <Route path="/catalog/:pid" element={<ProductDetail />} />
          <Route path="/" element={<AppRoot />}>
            <Route index element={<RoleRedirect />} />
            <Route path="dashboard" element={<Guard allow={canManageOrDM}><Dashboard /></Guard>} />
            <Route path="workspace" element={<Guard allow={(u) => isDivisionUser(u) && !isContentCreator(u)}><Workspace /></Guard>} />
            <Route path="content" element={<Guard allow={canManageContent}><Content /></Guard>} />
            <Route path="clients" element={<Guard allow={canManageOrDM}><Clients /></Guard>} />
            <Route path="clients/:id/statement" element={<Guard allow={canManageOrDM}><ClientStatement /></Guard>} />
            <Route path="bookings" element={<Bookings />} />
            <Route path="bookings/:id" element={<BookingDetail />} />
            <Route path="quotations" element={<Guard allow={canManageOrDM}><Quotations /></Guard>} />
            <Route path="quotations/:id" element={<Guard allow={canManageOrDM}><QuotationDetail /></Guard>} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="production" element={<Guard allow={(u) => isManagement(u) || isPbhMember(u)}><Production /></Guard>} />
            <Route path="employees" element={<Guard allow={(u) => isManagement(u) || u.role === "division_manager"}><Employees /></Guard>} />
            <Route path="employees/pending" element={<Guard allow={(u) => u.role === "owner"}><PendingUsers /></Guard>} />
            <Route path="pricelist" element={<Navigate to="/master-products" replace />} />
            <Route path="pricelist-legacy" element={<Guard allow={(u) => isManagement(u) || u.role === "division_manager" || u.role === "division_admin"}><Pricelist /></Guard>} />
            <Route path="inquiry" element={<Guard allow={(u) => isManagement(u) || u.role === "division_manager" || u.role === "division_admin"}><Inquiry /></Guard>} />
            <Route path="reports" element={<Guard allow={(u) => isManagement(u) || u.role === "division_manager" || u.role === "division_admin"}><Reports /></Guard>} />
            <Route path="employees/:id" element={<Guard allow={(u) => isManagement(u) || u.role === "division_manager"}><EmployeeDetail /></Guard>} />
            <Route path="divisions" element={<Guard allow={isManagement}><DivisionsOverview /></Guard>} />
            <Route path="finance" element={<Guard allow={(u) => hasFinancialAccess(u) || isDivManager(u)}><FinanceDashboard /></Guard>} />
            <Route path="invoices" element={<Guard allow={(u) => hasFinancialAccess(u) || isDivManager(u)}><Invoices /></Guard>} />
            <Route path="invoices/:id" element={<Guard allow={(u) => hasFinancialAccess(u) || isDivManager(u)}><InvoiceDetail /></Guard>} />
            <Route path="vendors" element={<Guard allow={(u) => hasFinancialAccess(u) || isDivManager(u)}><Vendors /></Guard>} />
            <Route path="wages" element={<Guard allow={(u) => hasFinancialAccess(u) || isDivManager(u)}><Wages /></Guard>} />
            <Route path="approvals" element={<Guard allow={hasFinancialAccess}><Approvals /></Guard>} />
            <Route path="recycle-bin" element={<Guard allow={(u) => ["owner","general_manager"].includes(u.role)}><RecycleBin /></Guard>} />
            <Route path="landing-cms" element={<Guard allow={(u) => ["owner","general_manager"].includes(u.role)}><LandingCMS /></Guard>} />
            <Route path="imports" element={<Guard allow={(u) => ["owner","general_manager","admin","division_manager"].includes(u.role)}><Imports /></Guard>} />
            <Route path="master-products" element={<Guard allow={(u) => isManagement(u) || isPbhManager(u)}><MasterProducts /></Guard>} />
            <Route path="inquiries/pool" element={<Guard allow={(u) => isManagement(u) || u.role === "division_manager" || u.role === "division_admin"}><InquiryPool /></Guard>} />
            <Route path="bookings/tasks" element={<BookingsTasks />} />
            <Route path="tasks/board" element={<TasksBoard />} />
            <Route path="schedules" element={<Navigate to="/calendar" replace />} />
            <Route path="overload-planner" element={<OverloadPlanner />} />
          </Route>
          <Route path="*" element={<RoleRedirect />} />
        </Routes>
  );
}

const RoleRedirect = () => {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={isContentCreator(user) ? "/content" : (isDivisionUser(user) && !isMuaAdmin(user)) ? "/workspace" : "/dashboard"} replace />;
};

export default App;
